import { Component } from 'react';
import { Button } from '../common/button';
import { QrCodeModalBloc, QrCodeComponent } from '../common/qr_code_modal';
import { BlocProvider, AppDep, StreamBuilder } from '../../provider';
import Log from '../../common/util/log';
import autoBind from 'auto-bind';
import { BehaviorSubject } from 'rxjs';
import { GroupInfo } from '../../controller/do';
import { GlobalContext } from '../../common/modal_boundary';
import TableData from '../common/TableData';
import { TableDataBloc, TableRegistry, TableIds } from '../../controller/table_data_bloc';
import ModalComponentV2 from '../common/ModalComponentV2';
import { ProcessingModalLayout } from '../common/ProcessingModal';

const TAG = 'DownloadWhatsappContacts';

export const whatsappContactsColumns = [
	{ label: 'Name', field: 'name' },
	{ label: 'Topic', field: 'topic' },
	{ label: 'Members', field: 'member_count' },
];

class Bloc {
	public qrCodeBloc: QrCodeModalBloc;
	public tableDataBloc: TableDataBloc<GroupInfo>;
	public readonly importModalVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public readonly importStatus: BehaviorSubject<{ message: string; complete: boolean } | undefined> =
		new BehaviorSubject<
			| {
					message: string;
					complete: boolean;
			  }
			| undefined
		>(undefined);
	public readonly selectedGroup: BehaviorSubject<GroupInfo | null> = new BehaviorSubject<GroupInfo | null>(null);
	private _api: any;

	constructor(appDep: AppDep) {
		autoBind(this);
		this.qrCodeBloc = new QrCodeModalBloc(appDep.api);
		this._api = appDep.api;
		this.tableDataBloc = new TableDataBloc(this.fetchWhatsappGroups, appDep.searchBoxQuery, ['name', 'topic'], {
			autoFetch: false,
		});
	}

	destroy() {
		this.tableDataBloc.destroy();
	}

	@GlobalContext.handlErrorAsModal()
	async handleShowQr() {
		Log.d(TAG, 'inside handleShowQr');
		try {
			const userId = this._api.getUserId();
			this.qrCodeBloc.setPendingMessage('Connecting to your WhatsApp groups', 'Cancel');
			await Promise.all([
				this.qrCodeBloc.doQrCodeValidation(userId),
				this.tableDataBloc.updateData().finally(() => this.qrCodeBloc.onModalClose()),
			]);
		} catch (error) {
			Log.e(TAG, 'Error showing QR code:', error);
			throw error;
		}
	}

	@GlobalContext.handlErrorAsModal([])
	async fetchWhatsappGroups(): Promise<GroupInfo[]> {
		Log.d(TAG, 'inside fetchWhatsappGroups');
		const groups = await this._api.getWhatsappGroup();
		return groups;
	}

	@GlobalContext.handlErrorAsModal()
	async importContacts(group: GroupInfo) {
		Log.d(TAG, 'inside importContacts', group);
		this.selectedGroup.next(group);
		this.importModalVisible.next(true);
		this.importStatus.next({ message: 'Importing contacts', complete: false });

		const result = await this._api.importWhatsAppGroup(group.jid);

		await TableRegistry.invalidateAll([TableIds.GROUPS, TableIds.CONTACTS]);

		const imported = result?.total_contacts_imported ?? 0;
		const skipped = result?.skipped_non_indian ?? 0;
		const successMessage =
			skipped > 0
				? `Contacts successfully imported. ${imported} contact${imported === 1 ? '' : 's'} imported. ${skipped} skipped (not Indian numbers).`
				: 'Contacts successfully imported.';

		this.importStatus.next({
			message: successMessage,
			complete: true,
		});
	}

	closeImportModal() {
		this.importModalVisible.next(false);
		this.importStatus.next(undefined);
		this.selectedGroup.next(null);
	}
}

export class DownloadWhatsappContacts extends Component<any, {}> {
	render() {
		return (
			<BlocProvider
				create={(appDep: AppDep) => new Bloc(appDep)}
				destroy={(bloc: Bloc) => bloc.destroy()}
				builder={(bloc: Bloc) => (
					<div className="m-2">
						<StreamBuilder
							stream={bloc.tableDataBloc.data}
							builder={(groups: GroupInfo[]) => {
								// Add Action column with Import button to each row
								const dataWithActions = {
									rows: groups.map(group => ({
										...group,
										action: (
											<Button
												focused
												className="btn button btn-sm"
												onClick={() => bloc.importContacts(group)}
											>
												Import
											</Button>
										),
									})),
									columns: [
										...whatsappContactsColumns,
										{ label: 'Action', field: 'action', sort: 'disabled' as const },
									],
								};

								return (
									<div>
										{groups.length > 0 ? (
											<div className="mt-4">
												<TableData
													bordered={true}
													striped={true}
													data={dataWithActions}
													searchableFields={['name', 'topic']}
												/>
											</div>
										) : (
											<div className="text-center mt-4">
												<h4>WhatsApp Groups</h4>
												<p className="text-muted">Connect with WhatsApp to download contacts from a group</p>
												<Button focused className="btn button" onClick={bloc.handleShowQr}>
													Connect to WhatsApp
												</Button>
											</div>
										)}
									</div>
								);
							}}
						/>

						<QrCodeComponent bloc={bloc.qrCodeBloc} />

						<StreamBuilder
							stream={bloc.importModalVisible}
							builder={(visible: boolean) => (
								<StreamBuilder
									stream={bloc.importStatus}
									builder={(status: { message: string; complete: boolean } | undefined) =>
										status ? (
											<ModalComponentV2
												isOpen={visible}
												toggle={bloc.closeImportModal}
												body={<ProcessingModalLayout message={status.message} />}
												buttonList={
													status.complete
														? [
																<Button
																	focused
																	className="btn button"
																	onClick={bloc.closeImportModal}
																>
																	OK
																</Button>,
														  ]
														: []
												}
											/>
										) : (
											<></>
										)
									}
								/>
							)}
						/>
					</div>
				)}
			/>
		);
	}
}
